import React, { useRef, useEffect } from 'react'
import * as d3 from 'd3'

const tooltipStyles = `
  .chart-tooltip {
    position: absolute;
    background-color: #334155;
    color: white;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 14px;
    pointer-events: none; /* Allows mouse events to pass through to elements below */
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    z-index: 9999; /* Ensure tooltip appears on top of other elements */
  }
`

// --- TypeScript Interfaces ---

// Describes the shape of a single data item for our chart
interface DataItem {
  name: string
  value: number
  color: string // Each data point now has a configurable color
}

// Describes the props accepted by our PieChart component
interface PieChartProps {
  data: DataItem[]
  width: number
  height: number
  chartName: string | string[] // Allow string or array of strings for the name
}

// PieChart component (now a TypeScript Functional Component)
const PieChart: React.FC<PieChartProps> = ({
  data,
  width,
  height,
  chartName
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null)

  useEffect(() => {
    if (!data || data.length === 0 || !svgRef.current) return

    const outerRadius = (Math.min(width, height) / 2) * 0.95
    const innerRadius = outerRadius * 0.6

    // --- D3 Setup ---
    const svg = d3
      .select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .html('') // Clear previous SVG content

    const g = svg
      .append('g')
      .attr('transform', `translate(${width / 2}, ${height / 2})`)

    // --- Pie Generator ---
    const tooltip = d3
      .select('body')
      .append('div')
      .attr('class', 'chart-tooltip')

    const pie = d3
      .pie<DataItem>()
      .value(d => d.value)
      .sort(null)

    // --- Arc Generator ---
    const arc = d3
      .arc<d3.PieArcDatum<DataItem>>()
      .innerRadius(innerRadius)
      .outerRadius(outerRadius)

    const arcHover = d3
      .arc<d3.PieArcDatum<DataItem>>()
      .innerRadius(innerRadius)
      .outerRadius(outerRadius * 1.05)

    // --- Draw Slices ---
    const arcs = g
      .selectAll('.arc')
      .data(pie(data))
      .enter()
      .append('g')
      .attr('class', 'arc')

    arcs
      .append('path')
      .attr('fill', d => d.data.color)
      .attr('stroke', 'white')
      .style('stroke-width', '2px')
      .on('mouseover', function (event, d) {
        d3.select(this).raise()
        d3.select(this)
          .transition()
          .duration(200)
          .attr('d', arcHover as any)
        tooltip.style('opacity', 1)
      })
      .on('mousemove', function (event, d) {
        tooltip
          .html(
            `<strong>${d.data.name}</strong>: ${d.data.value.toLocaleString()}`
          )
          .style('left', `${event.pageX + 15}px`)
          .style('top', `${event.pageY}px`)
      })
      .on('mouseout', function () {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('d', arc as any)
        tooltip.style('opacity', 0)
      })
      .transition()
      .duration(1000)
      .attrTween('d', function (d) {
        const i = d3.interpolate((this as any)._current, d)
        ;(this as any)._current = i(0)
        return t => arc(i(t))!
      })

    // --- Add Chart Name in the Center (with multi-line support) ---
    const text = g
      .append('text')
      .attr('text-anchor', 'middle')
      .style('font-size', `${Math.min(width, height) / 14}px`) // Adjusted font size for multi-line
      .style('font-weight', 'bold')
      .style('fill', '#334155')

    const lines = Array.isArray(chartName) ? chartName : [chartName]
    const lineHeight = 1.1 // ems

    // Calculate the starting 'dy' to vertically center the text block
    const startDy = (-(lines.length - 1) / 2) * lineHeight

    lines.forEach((line, i) => {
      text
        .append('tspan')
        .attr('x', 0)
        // Set the dy for the first line to the calculated start, and subsequent lines to the line height
        .attr('dy', `${i === 0 ? startDy : lineHeight}em`)
        .text(line)
    })

    return () => {
      tooltip.remove()
    }
  }, [data, width, height, chartName])

  return (
    <>
      <style>{tooltipStyles}</style>
      <div className="flex justify-center items-center">
        <svg ref={svgRef}></svg>
      </div>
    </>
  )
}

export default PieChart
